import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useOktaAuth, Security, SecureRoute as SecureRoute$1, LoginCallback as LoginCallback$1 } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';

const defaultAuthState = {
  isAuthenticated: false,
  idToken: void 0,
  accessToken: void 0
};
const AuthContext = createContext({
  userManager: {},
  authState: defaultAuthState,
  userContext: null
});
const useAuth = () => useContext(AuthContext);
const AuthContextProvider = ({ userManager, children }) => {
  const oktaAuth = userManager._oktaAuth.oktaAuth;
  const [authState, setAuthState] = useState(defaultAuthState);
  const [userContext, setUserContext] = useState(null);
  const { authState: oktaAuthState } = useOktaAuth();
  useEffect(() => {
    userManager.getAuthState().then(setAuthState);
    userManager.getUser().then(setUserContext);
  }, [userManager, oktaAuth, oktaAuthState]);
  return /* @__PURE__ */ React.createElement(
    AuthContext.Provider,
    {
      value: {
        userManager,
        authState,
        userContext
      }
    },
    children
  );
};

var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};
const SecurityProvider = ({
  userManager,
  children
}) => {
  const oktaAuthService = userManager._oktaAuth;
  const restoreOriginalUri = (oktaAuth, originalUri) => __async(void 0, null, function* () {
    var _a, _b, _c;
    const path = originalUri != null ? originalUri : "/";
    return (_c = (_b = (_a = userManager.options).restoreOriginalUri) == null ? void 0 : _b.call(_a, oktaAuth, path)) != null ? _c : window.location.replace(toRelativeUrl(path, window.location.origin));
  });
  return /* @__PURE__ */ React.createElement(
    Security,
    {
      oktaAuth: oktaAuthService.oktaAuth,
      onAuthRequired: oktaAuthService.onAuthRequired,
      restoreOriginalUri
    },
    /* @__PURE__ */ React.createElement(AuthContextProvider, { userManager }, children)
  );
};

var __defProp$1 = Object.defineProperty;
var __getOwnPropSymbols$1 = Object.getOwnPropertySymbols;
var __hasOwnProp$1 = Object.prototype.hasOwnProperty;
var __propIsEnum$1 = Object.prototype.propertyIsEnumerable;
var __defNormalProp$1 = (obj, key, value) => key in obj ? __defProp$1(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues$1 = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp$1.call(b, prop))
      __defNormalProp$1(a, prop, b[prop]);
  if (__getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(b)) {
      if (__propIsEnum$1.call(b, prop))
        __defNormalProp$1(a, prop, b[prop]);
    }
  return a;
};
var __objRest$1 = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp$1.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols$1)
    for (var prop of __getOwnPropSymbols$1(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum$1.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const SecureRoute = (_a) => {
  var _b = _a, { children } = _b, props = __objRest$1(_b, ["children"]);
  return /* @__PURE__ */ React.createElement(SecureRoute$1, __spreadValues$1({}, props), children);
};

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const LoginCallback = (_a) => {
  var _b = _a, { children } = _b, props = __objRest(_b, ["children"]);
  return /* @__PURE__ */ React.createElement(LoginCallback$1, __spreadValues({}, props), children);
};

const PrimeAuthLoginCallback = () => {
  const { userManager } = useAuth();
  useEffect(() => {
    var _a;
    (_a = userManager == null ? void 0 : userManager.primeAuth) == null ? void 0 : _a.endBackgroundLogin();
  });
  return /* @__PURE__ */ React.createElement(React.Fragment, null);
};

const DELAY_BEFORE_FIRST_CHECK_MS = 5 * 1e3;
const RE_CHECK_INTERVAL_MS = 60 * 60 * 1e3;
const usePrimeAuthSession = () => {
  const [needsRefresh, setNeedsRefresh] = useState(true);
  const [isPending, setIsPending] = useState(false);
  const { authState, userManager } = useAuth();
  const pathname = window.location.pathname;
  useEffect(() => {
    var _a;
    if (needsRefresh && authState.isAuthenticated) {
      setNeedsRefresh(false);
      setIsPending(true);
      (_a = userManager.primeAuth) == null ? void 0 : _a.isAuthenticated().then((isAuthenticated) => {
        if (!isAuthenticated) {
          setTimeout(() => {
            var _a2;
            (_a2 = userManager.primeAuth) == null ? void 0 : _a2.startBackgroundLogin().then(() => {
              setTimeout(() => {
                setNeedsRefresh(true);
              }, RE_CHECK_INTERVAL_MS);
            }).finally(() => {
              setIsPending(false);
            });
          }, DELAY_BEFORE_FIRST_CHECK_MS);
        } else {
          setIsPending(false);
        }
      });
    }
  }, [needsRefresh, pathname, authState.isAuthenticated, userManager]);
  return useMemo(
    () => ({
      isPending
    }),
    [isPending]
  );
};

const useUserClaims = () => {
  const [claims, setClaims] = useState();
  const { authState } = useAuth();
  useEffect(() => {
    var _a;
    if (authState.isAuthenticated) {
      const claims2 = (_a = authState.idToken) == null ? void 0 : _a.claims;
      if (claims2) {
        setClaims(claims2);
      }
    }
  }, [authState.isAuthenticated, authState.idToken]);
  return useMemo(
    () => ({
      claims,
      isLoading: claims == null
    }),
    [claims]
  );
};

export { LoginCallback, PrimeAuthLoginCallback, SecureRoute, SecurityProvider, useAuth, usePrimeAuthSession, useUserClaims };
